/**
 * The Employee Mileage and Expense Report's own rules (piece 24; decision
 * 0199): the category that is an account, the funding source that decides
 * which liability is credited, the meal documentation IRC §274(d) mandates,
 * the four review flags that warn and never block, and the mileage rate
 * stamped from the day the travel happened.
 *
 * 🔨 Every rule here is pure. The service writes what these permit, the screen
 * renders what they answer, and neither invents a state of its own.
 */

import { cents, formatCurrency, roundHalfUpCents } from '../money';

/** Settings › Accounting › Expense categories edits the threshold beside the category list (settled 2026-09-14). */
export const MEAL_THRESHOLD_KEY = 'expenses.mealPerPersonThreshold';
/** 75 dollars a head, in cents, as every money setting is held. */
export const MEAL_THRESHOLD_DEFAULT_CENTS = 7_500;

/** Settings and administration holds the mileage rate as an effective-dated series (piece 24, the report). */
export const MILEAGE_RATE_KEY = 'expenses.mileageRatePerMile';
/** Cents a mile; the seeded figure is the 2026 federal business rate, and a person edits the series. */
export const MILEAGE_RATE_DEFAULT_CENTS = 70;

/** 🔨 A meal receipt is required at $75 or more, which is the IRS's own figure and not the per-person threshold. */
export const MEAL_RECEIPT_REQUIRED_CENTS = 7_500;

export const EXPENSE_FUNDING_SOURCES = ['CompanyCard', 'Reimbursable'] as const;
export type ExpenseFundingSourceValue = (typeof EXPENSE_FUNDING_SOURCES)[number];

// Decision 12720: NoJournalFound is raised by the Zoho Expense pull alone, on an imported line whose journal entry it did not find.
export const EXPENSE_FLAG_KINDS = ['DuplicateReceipt', 'AlcoholIncluded', 'ExceedsPerPersonThreshold', 'NoLinkedBusinessContext', 'NoJournalFound'] as const;
export type ExpenseFlagKindValue = (typeof EXPENSE_FLAG_KINDS)[number];

/** 🔒 A Reimbursable line credits 2250 and a CompanyCard line credits 2400 (decision 0199). */
export const REIMBURSEMENT_PAYABLE_ACCOUNT = '2250';
export const CREDIT_CARD_PAYABLE_ACCOUNT = '2400';
/** The account a category buying stock posts to; a line in it needs an item, a quantity and a site. */
export const INVENTORY_ACCOUNT = '1200';

/**
 * 🔒 Each seeded category posts to the chart account of its own name (decision
 * 2871): 6600 Travel Expense, 6610 Meals Expense, 6640 Mileage Reimbursement. The
 * chart names no lodging account and no other supplies account, so Lodging posts
 * to Travel Expense and Supplies to 6910 Office Supplies Expense.
 */
export const TRAVEL_ACCOUNT = '6600';
/** 🔨 The meal rules recognise a meal by this account, never by the category's name. */
export const MEALS_ACCOUNT = '6610';
/** The chart account the seeded Mileage category posts to (decision 2871). */
export const MILEAGE_ACCOUNT = '6640';
/**
 * 🔨 + Mileage finds its category by this name, never by its account (decision 2871, amending 1315's lookup): an
 * administrator may point the category at another account, and the person never picks it.
 */
export const MILEAGE_CATEGORY_NAME = 'Mileage';
/** Decision 7920: the per diem lines' category, found by its name as Mileage is, on 6600 Travel Expense beside Lodging. */
export const PER_DIEM_CATEGORY_NAME = 'Per diem';
export const OFFICE_SUPPLIES_ACCOUNT = '6910';

/**
 * The seeded categories, each on the chart account of its own name, plus Parts
 * and materials pointing at 1200 Inventory (decisions 0199, 2871). 🔨 Each
 * category is an account, which is how a line finds its posting; no line
 * carries a bare category name.
 */
export interface ExpenseCategorySeed {
  name: string;
  accountNumber: string;
  requiresReceipt: boolean;
  isBillableDefault: boolean;
  sortOrder: number;
}

export const EXPENSE_CATEGORY_SEED: readonly ExpenseCategorySeed[] = [
  { name: 'Travel', accountNumber: TRAVEL_ACCOUNT, requiresReceipt: true, isBillableDefault: true, sortOrder: 10 },
  { name: MILEAGE_CATEGORY_NAME, accountNumber: MILEAGE_ACCOUNT, requiresReceipt: false, isBillableDefault: true, sortOrder: 20 },
  { name: 'Lodging', accountNumber: TRAVEL_ACCOUNT, requiresReceipt: true, isBillableDefault: true, sortOrder: 30 },
  // Decision 7920: a daily allowance, claimed at a rate and owing no receipt; the chart names no per diem account.
  { name: PER_DIEM_CATEGORY_NAME, accountNumber: TRAVEL_ACCOUNT, requiresReceipt: false, isBillableDefault: true, sortOrder: 35 },
  { name: 'Meals and entertainment', accountNumber: MEALS_ACCOUNT, requiresReceipt: true, isBillableDefault: false, sortOrder: 40 },
  { name: 'Supplies', accountNumber: OFFICE_SUPPLIES_ACCOUNT, requiresReceipt: true, isBillableDefault: false, sortOrder: 50 },
  // 💰 Stock bought on an expense line has no purchase order behind it: no vendor price history and no landed cost.
  { name: 'Parts and materials', accountNumber: INVENTORY_ACCOUNT, requiresReceipt: true, isBillableDefault: true, sortOrder: 60 },
];

/**
 * 🔒 Decision 7920: the one per diem rate the seed writes into an empty table, for the owner to edit; a table that
 * already holds a row is left as it stands, so an edited or retired rate is never written a second time.
 */
export const PER_DIEM_RATE_SEED = { name: 'Standard', dailyAmountCents: 6800, effectiveFrom: '2026-01-01' } as const;

// ---- the line's own rules ----

export interface ExpenseLineFacts {
  categoryAccountNumber: string;
  fundingSource: ExpenseFundingSourceValue;
  amountCents: number;
  incurredOn: string;
  description?: string | null;
  itemId?: string | null;
  quantity?: number | null;
  siteId?: string | null;
  receiptFileId?: string | null;
  categoryRequiresReceipt?: boolean;
  mileageMiles?: number | null;
  merchantName?: string | null;
  place?: string | null;
  businessPurpose?: string | null;
  partySize?: number | null;
  attendeeCount?: number;
  /** How many of the attendees carry a blank business relationship, which a meal refuses (row T2724). */
  attendeesWithoutRelationship?: number;
}

/**
 * 🔨 A line whose category posts to 1200 requires `itemId`, `quantity` and
 * `siteId`, because the approved line writes a Receipt `StockMovement` at that
 * site and a movement needs all three (decision 0199).
 */
export function stockLineProblem(facts: ExpenseLineFacts): string | null {
  if (facts.categoryAccountNumber !== INVENTORY_ACCOUNT) return null;
  if (!facts.itemId) return 'a line in a category that posts to 1200 Inventory names the item it bought';
  if (typeof facts.quantity !== 'number' || !(facts.quantity > 0)) return 'a line in a category that posts to 1200 Inventory names how many it bought';
  if (!facts.siteId) return 'a line in a category that posts to 1200 Inventory names the site the stock landed at';
  return null;
}

/**
 * 🔨 Meal Documentation requires the five elements IRC §274(d) mandates:
 * Amount, Date, Place, Business Purpose, and Business Relationship tied to
 * real Person records, never headcount alone.
 */
export function mealDocumentationProblem(facts: ExpenseLineFacts): string | null {
  if (facts.categoryAccountNumber !== MEALS_ACCOUNT) return null;
  if (!facts.place?.trim()) return 'a meal line names the place, which is one of the five elements the tax code asks for';
  if (!facts.businessPurpose?.trim()) return 'a meal line names the business purpose, which is one of the five elements the tax code asks for';
  if (!(facts.attendeeCount && facts.attendeeCount > 0)) return 'a meal line names who was there as real people, because a headcount is never accepted in place of the business relationship; add the attendees by name';
  if (facts.attendeesWithoutRelationship && facts.attendeesWithoutRelationship > 0) return 'a meal line names the business relationship of each attendee, which is one of the five elements the tax code asks for; fill in how each person is connected to the business';
  return null;
}

/** 🔨 A meal receipt is required at $75 or more, whatever the category's own receipt default says. */
export function receiptProblem(facts: ExpenseLineFacts): string | null {
  if (facts.receiptFileId) return null;
  if (facts.categoryAccountNumber === MEALS_ACCOUNT && Math.abs(facts.amountCents) >= MEAL_RECEIPT_REQUIRED_CENTS) {
    return 'a meal of $75 or more carries its receipt; that is the figure the tax code sets, not a policy of ours';
  }
  if (facts.categoryRequiresReceipt && !facts.mileageMiles) return 'this category asks for a receipt on every line';
  return null;
}

/** 🔨 A line names what it was and whose money it was; the funding source is required, with no default. */
export function expenseLineProblem(facts: ExpenseLineFacts): string | null {
  return expenseLineWriteProblem(facts) ?? receiptProblem(facts);
}

/**
 * 🔨 What a line is refused for when it is entered on a report (T2716): everything `expenseLineProblem` holds
 * except the receipt, which pieces 24 and 35 check at Submit and never when a line is written.
 */
export function expenseLineWriteProblem(facts: ExpenseLineFacts): string | null {
  if (!facts.description?.trim()) return 'an expense line says what it was for';
  if (!Number.isInteger(facts.amountCents) || facts.amountCents === 0) return 'an expense line carries an amount in whole cents; enter the amount that was spent';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(facts.incurredOn)) return 'an expense line names the day it was incurred, as YYYY-MM-DD; pick the date the money was spent';
  if (!(EXPENSE_FUNDING_SOURCES as readonly string[]).includes(facts.fundingSource)) {
    return `an expense line names whose money it was: ${EXPENSE_FUNDING_SOURCES.join(' or ')}; there is no default`;
  }
  return stockLineProblem(facts) ?? mealDocumentationProblem(facts);
}

/** 🔨 Whether a line owes a receipt (T2716): the rule `receiptProblem` holds, read per line at Submit and by the report page. */
export function receiptOwed(facts: ExpenseLineFacts): boolean {
  return receiptProblem(facts) !== null;
}

/**
 * 🔨 Submit refuses a report holding a line that owes its receipt, naming the first by description and day and
 * counting the rest, as the unreviewed-line refusal does (T2716).
 */
export function receiptsOwedProblem(lines: readonly (ExpenseLineFacts & { merchantName?: string | null })[]): string | null {
  const owing = lines.filter((line) => receiptOwed(line));
  if (owing.length === 0) return null;
  const first = [...owing].sort((a, b) => (a.incurredOn < b.incurredOn ? -1 : a.incurredOn > b.incurredOn ? 1 : 0))[0]!;
  const named = (first.description ?? '').trim() || first.merchantName?.trim() || 'a line';
  const rest = owing.length - 1;
  const more = rest === 0 ? '' : rest === 1 ? ', and one more line needs one too' : `, and ${rest} more lines need one too`;
  return `The expense ${named} of ${first.incurredOn} still needs its receipt: attach it before the report goes${more}.`;
}

/**
 * The amount of a mileage line: the miles at the rate in force on the day the
 * travel happened, rounded half up. 🔨 January travel filed in March takes
 * January's rate, and a later rate change moves no existing line.
 */
export function mileageAmountCents(miles: number, rateCentsPerMile: number): number {
  return roundHalfUpCents(miles * rateCentsPerMile);
}

/** The per-person figure Party Size drives, computed and stored beside the line. */
export function perPersonAmountCents(amountCents: number, partySize: number | null | undefined): number | null {
  if (!partySize || partySize <= 0) return null;
  return roundHalfUpCents(amountCents / partySize);
}

// ---- the four review flags ----

export interface RaisedFlag {
  flagKind: ExpenseFlagKindValue;
  duplicateOfExpenseLineId?: string | null;
  reason: string;
}

/**
 * 🔨 Duplicate-receipt detection matches vendor, amount and date across the
 * employee's own reports; it warns and never blocks. 💰 A duplicate is almost
 * never fraud: a phone uploaded the same photo twice and the submitter will
 * not notice. The approver is the only backstop, and only when the flag shows
 * it to them.
 */
export function duplicateOf(
  line: { id?: string | null; merchantName?: string | null; amountCents: number; incurredOn: string },
  others: readonly { id: string; merchantName: string | null; amountCents: number; incurredOn: string }[],
): string | null {
  if (!line.merchantName?.trim()) return null;
  const match = others.find((other) =>
    other.id !== line.id
    && (other.merchantName ?? '').trim().toLowerCase() === line.merchantName!.trim().toLowerCase()
    && other.amountCents === line.amountCents
    && other.incurredOn === line.incurredOn);
  return match ? match.id : null;
}

/**
 * Every flag a line raises at submission. 🔨 None of them blocks: the line is
 * submitted, the breach is recorded on it, and the approver decides. A
 * NoLinkedBusinessContext flag is cleared by hand, by adding the missing link,
 * and never auto-clears.
 */
export function flagsFor(
  facts: ExpenseLineFacts & { id?: string | null; alcoholIncluded?: boolean | null; hasLinkedContext?: boolean; sameDayCalendarEvent?: boolean },
  options: { mealThresholdCents: number; otherLines: readonly { id: string; merchantName: string | null; amountCents: number; incurredOn: string }[] },
): RaisedFlag[] {
  const raised: RaisedFlag[] = [];
  const duplicate = duplicateOf({ ...facts, id: facts.id ?? null }, options.otherLines);
  if (duplicate) {
    raised.push({ flagKind: 'DuplicateReceipt', duplicateOfExpenseLineId: duplicate, reason: 'the same vendor, amount and date already appear on a line of this employee’s; the pair is shown side by side rather than refused' });
  }
  if (facts.alcoholIncluded) raised.push({ flagKind: 'AlcoholIncluded', reason: 'alcohol was detected on the itemized receipt' });
  const perPerson = perPersonAmountCents(facts.amountCents, facts.partySize);
  if (perPerson !== null && perPerson > options.mealThresholdCents) {
    raised.push({ flagKind: 'ExceedsPerPersonThreshold', reason: `the per-person amount is ${perPerson} cents against a threshold of ${options.mealThresholdCents}` });
  }
  // 🔨 A Meals line checks for a same-day Calendar event; a product-purchase line checks for a Tagged Record.
  const contexted = facts.categoryAccountNumber === MEALS_ACCOUNT ? facts.sameDayCalendarEvent === true : facts.hasLinkedContext === true;
  if (!contexted) {
    raised.push({
      flagKind: 'NoLinkedBusinessContext',
      reason: facts.categoryAccountNumber === MEALS_ACCOUNT
        ? 'no calendar event that day carries this meal, so nothing says what business it was for'
        : 'no record is tagged on this line, so nothing says which job or deal it was for',
    });
  }
  return raised;
}

/** 🔨 A flag's resolution starts Pending and never moves on its own; the approver approves or rejects each flagged line. */
export function flagResolutionProblem(resolution: string): string | null {
  if (resolution === 'Approved' || resolution === 'Rejected') return null;
  return 'a flag is resolved by approving or rejecting the line it is on; it never clears on its own';
}

// ---- the report ----

/**
 * 🔨 The approved line debits its category's account, or 1200, and credits
 * 2250 for a Reimbursable line or 2400 for a CompanyCard line (decision 0199).
 * A card line still posts only once it is matched to its feed transaction.
 */
export function creditAccountFor(fundingSource: ExpenseFundingSourceValue): string {
  return fundingSource === 'CompanyCard' ? CREDIT_CARD_PAYABLE_ACCOUNT : REIMBURSEMENT_PAYABLE_ACCOUNT;
}

export function expenseRouteFor(fundingSource: ExpenseFundingSourceValue): 'expense.companyCard' | 'expense.reimbursable' {
  return fundingSource === 'CompanyCard' ? 'expense.companyCard' : 'expense.reimbursable';
}

/**
 * 🔨 A company-card expense line is not posted until it is matched to a feed
 * transaction, and the match is what posts. 💰 The reason is a double count:
 * one spend arrives twice, as the employee's expense line and as the charge on
 * the card feed, and posting both books the expense twice with no error.
 */
export function postsOnApproval(fundingSource: ExpenseFundingSourceValue): boolean {
  return fundingSource === 'Reimbursable';
}

/** A report is submitted with at least one line, and its total is the sum of them. */
export function reportTotalCents(lines: readonly { amountCents: number }[]): number {
  return lines.reduce((sum, line) => sum + line.amountCents, 0);
}

export function submitProblem(lines: readonly unknown[]): string | null {
  return lines.length === 0 ? 'a report with no lines has nothing to approve' : null;
}

/**
 * 🔒 Decision 1475: a line the Expenses page's drop zone filed carries no
 * category and no Paid With until somebody opens it and saves, so a report
 * holding one is not submitted. The refusal names the first such line and
 * counts the rest, the way the unreported-line guard does.
 */
export function unreviewedLineProblem(lines: readonly { description: string; merchantName: string | null; incurredOn: string; categoryId: string | null; fundingSource: string | null }[]): string | null {
  const unreviewed = lines.filter((line) => line.categoryId === null || line.fundingSource === null);
  if (unreviewed.length === 0) return null;
  const first = [...unreviewed].sort((a, b) => (a.incurredOn < b.incurredOn ? -1 : a.incurredOn > b.incurredOn ? 1 : 0))[0]!;
  const named = (first.description ?? '').trim() || first.merchantName?.trim() || 'a receipt';
  const rest = unreviewed.length - 1;
  const more = rest === 0 ? '' : rest === 1 ? ', and one more line needs it too' : `, and ${rest} more lines need it too`;
  return `The expense ${named} of ${first.incurredOn} still needs review: open it and save it with its category and Paid With before the report goes${more}.`;
}

/**
 * 🔨 The `ExpenseLineMatch` rows against one `BankTransaction` never sum past
 * its `amount`, and a match that would take them past it is refused. It is the
 * refusal `PaymentAllocation` already carries against a `Payment`.
 */
export function matchCapProblem(chargeCents: number, alreadyMatchedCents: number, addingCents: number): string | null {
  const charge = Math.abs(chargeCents);
  const open = charge - Math.abs(alreadyMatchedCents);
  if (Math.abs(addingCents) <= open) return null;
  return `this charge is ${charge} cents and ${Math.abs(alreadyMatchedCents)} is already matched, so ${open} is still open; ${Math.abs(addingCents)} would take the lines past the charge`;
}

/**
 * 🔨 A company-card line is matched from `PendingMatch` and from no other
 * state, because the report's approval is what writes that state and the match
 * is what posts. A `Draft` or a `Submitted` line matched would reach the ledger
 * with nobody's approval behind it, and on a report that can still be rejected.
 */
export function cardMatchStatusProblem(status: string): string | null {
  if (status === 'PendingMatch') return null;
  if (status === 'Posted') return 'this line is already matched and posted';
  return `a card line is matched once its report is approved, and this one is still ${status}; the approval is what moves it to Pending Match`;
}

/** 💰 An unmatched company-card item ages in both directions, and the report reads whichever side is asked for. */
export function agingDays(onDate: string, today: string): number {
  return Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${onDate}T00:00:00Z`)) / 86_400_000);
}

// ---- + Mileage (decision 1315) ----

/** What + Mileage asks, as typed: the day, where from and to, the miles and why. */
export interface MileageEntry {
  incurredOn: string;
  fromPlace: string;
  toPlace: string;
  miles: number | null;
  purpose: string;
}

/**
 * 🔒 Why a + Mileage entry cannot be saved yet (decision 1315), or null. Miles
 * and Purpose are required; From and To are words and either may stand empty,
 * because nothing computes miles from them.
 */
export function mileageEntryProblem(entry: MileageEntry): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(entry.incurredOn)) return 'A mileage expense names the day of the drive.';
  if (entry.miles === null || !Number.isFinite(entry.miles) || entry.miles <= 0) return 'A mileage expense names the miles driven, above zero.';
  if (!entry.purpose.trim()) return 'A mileage expense names why the drive was made.';
  return null;
}

/** 🔒 The line's description (decision 1315): "From to To: Purpose", with whichever of From and To were typed, and the purpose alone where neither was. */
export function mileageDescription(entry: Pick<MileageEntry, 'fromPlace' | 'toPlace' | 'purpose'>): string {
  const from = entry.fromPlace.trim();
  const to = entry.toPlace.trim();
  const purpose = entry.purpose.trim();
  const route = from && to ? `${from} to ${to}` : from ? `From ${from}` : to ? `To ${to}` : '';
  return route ? `${route}: ${purpose}` : purpose;
}

/**
 * 🔒 Decision 7920: the miles an Odometer Start and Odometer End pair works out, the difference to a tenth, or null
 * where either reading is missing or the end is not above the start.
 */
export function odometerMiles(start: number | null | undefined, end: number | null | undefined): number | null {
  if (typeof start !== 'number' || typeof end !== 'number' || !Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
  return Math.round((end - start) * 10) / 10;
}

/**
 * 🔨 Decision 7920: an odometer reading is a number of miles, zero or above; a pair reads its end above its start, and
 * the miles saved beside a pair are its difference. A reading given alone is stored as given.
 */
export function odometerProblem(entry: { odometerStart?: number | null; odometerEnd?: number | null; miles: number | null }): string | null {
  const readings = [entry.odometerStart, entry.odometerEnd].filter((reading): reading is number => reading !== null && reading !== undefined);
  if (readings.some((reading) => typeof reading !== 'number' || !Number.isFinite(reading) || reading < 0)) return 'An odometer reading is a number of miles, zero or above.';
  if (readings.length < 2) return null;
  const worked = odometerMiles(entry.odometerStart, entry.odometerEnd);
  if (worked === null) return 'Odometer End reads above Odometer Start.';
  if (entry.miles === null || Math.abs(entry.miles - worked) > 0.05) return `The miles of an odometer pair are its difference, ${worked}.`;
  return null;
}

// ---- Per Diem (decision 7920) ----

/** One per diem rate as `expenses/per-diem-rates` answers it: a daily amount in cents and the days it is in force. */
export interface PerDiemRateRow {
  id: string;
  /** The counter of piece 7, which Ending a rate names (decision 8120); absent from a row a cache kept before it. */
  version?: number;
  name: string;
  dailyAmountCents: number;
  currencyCode: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  isActive: boolean;
}

/** 🔒 A rate is offered for a day when it is active and the day falls between its first day and its last, an empty last day running on. */
export function perDiemRateInForce(rate: Pick<PerDiemRateRow, 'isActive' | 'effectiveFrom' | 'effectiveTo'>, day: string): boolean {
  return rate.isActive && rate.effectiveFrom <= day && (rate.effectiveTo === null || day <= rate.effectiveTo);
}

/** The days two calendar days span, both counted, or null where either is not a day or the last falls before the first. */
export function perDiemSpanDays(firstDay: string, lastDay: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(firstDay) || !/^\d{4}-\d{2}-\d{2}$/.test(lastDay)) return null;
  const span = Math.round((Date.parse(`${lastDay}T00:00:00Z`) - Date.parse(`${firstDay}T00:00:00Z`)) / 86_400_000) + 1;
  return Number.isFinite(span) && span >= 1 ? span : null;
}

/** What Per Diem asks, as typed: the first day, the last day or none for one day, the days claimed and why. */
export interface PerDiemEntry {
  firstDay: string;
  lastDay: string | null;
  days: number | null;
  purpose: string;
}

/** 🔨 The days are a positive number of half days, no more than the two dates span; the purpose says why the travel was made. */
export function perDiemEntryProblem(entry: PerDiemEntry): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(entry.firstDay)) return 'A per diem names its first day.';
  const span = perDiemSpanDays(entry.firstDay, entry.lastDay || entry.firstDay);
  if (span === null) return 'A per diem’s last day falls on or after its first.';
  if (entry.days === null || !Number.isFinite(entry.days) || entry.days <= 0 || !Number.isInteger(entry.days * 2)) return 'A per diem claims its days in whole or half days, above zero.';
  if (entry.days > span) return `A per diem claims no more days than its dates span, ${span}.`;
  if (!entry.purpose.trim()) return 'A per diem names why the travel was made.';
  return null;
}

/** 🔒 The amount: the days at the rate's daily amount, rounded half up, stamped by the server and never taken from the phone. */
export function perDiemAmountCents(days: number, dailyAmountCents: number): number {
  return roundHalfUpCents(days * dailyAmountCents);
}

/** The line's description: *Per diem, 3 days: Customer training in Toledo*. */
export function perDiemDescription(entry: Pick<PerDiemEntry, 'days' | 'purpose'>): string {
  const days = entry.days ?? 0;
  return `Per diem, ${days} ${days === 1 ? 'day' : 'days'}: ${entry.purpose.trim()}`;
}

// ---- Trips (decision 7920) ----

/** 🔨 New Trip names the trip and the day it starts. */
export function tripStartProblem(entry: { name: string; startedOn: string }): string | null {
  if (!entry.name.trim()) return 'A trip names the journey it is.';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(entry.startedOn)) return 'A trip names the day it starts, as a calendar day.';
  return null;
}

/** 🔨 A card charge with no receipt after thirty days is a chase, and only the report finds it. */
export const UNMATCHED_CHASE_DAYS = 30;

// ---- the unreported expense (decision 1313) ----

/** One of a person's unreported expenses, as the submit guard and the chase read it. */
export interface UnreportedLine {
  id: string;
  incurredOn: string;
  description: string;
  amountCents: number;
}

/**
 * 🔨 A report is not submitted while its filer holds an unreported expense
 * dated inside its period (decision 1313). The refusal names the earliest
 * such expense and counts the rest, so a receipt entered and forgotten is
 * found before the period's report goes to the approver. Null when none is.
 */
export function unreportedInPeriodProblem(period: { periodStart: string; periodEnd: string }, unreported: readonly UnreportedLine[]): string | null {
  const inside = unreported
    .filter((line) => period.periodStart <= line.incurredOn && line.incurredOn <= period.periodEnd)
    .sort((a, b) => (a.incurredOn === b.incurredOn ? a.id.localeCompare(b.id) : a.incurredOn.localeCompare(b.incurredOn)));
  const first = inside[0];
  if (!first) return null;
  const more = inside.length === 1 ? '' : inside.length === 2 ? ', and one more expense does too' : `, and ${inside.length - 1} more expenses do too`;
  return `The expense “${first.description}” of ${formatCurrency(cents(first.amountCents))} on ${first.incurredOn} is unreported and falls inside this report’s period${more}. Add it to this report or delete it, and then submit.`;
}

/**
 * 🔒 The unreported expenses the aging chase lists (decision 1313): those at
 * least as old as the card chase's threshold, oldest first, each with its age.
 * An unreported expense is a thing that can be forgotten, so the chase that
 * already finds a card charge with no receipt finds these too.
 */
export function unreportedChase<T extends UnreportedLine>(lines: readonly T[], today: string): (T & { ageDays: number })[] {
  return lines
    .map((line) => ({ ...line, ageDays: agingDays(line.incurredOn, today) }))
    .filter((line) => line.ageDays >= UNMATCHED_CHASE_DAYS)
    .sort((a, b) => b.ageDays - a.ageDays);
}

/**
 * 🔨 A card charge has one state whichever door reaches it (decision 0542).
 * The expense match reads the charge's own `matchStatus` and its cleared
 * reconciliation the way Banking's own acts do, so a charge a Pay Bills card
 * run already matched to a Payment, a charge somebody categorised by hand and a
 * charge excluded as a duplicate are each refused here by the state they are
 * in.
 *
 * 🔨 A charge already carrying expense matches is the one-to-many case and is
 * let through while the cap allows: one hotel charge covering a room, parking
 * and a meal is three lines against one charge, and refusing the second line
 * would break the rule the piece states.
 */
export function expenseChargeRefusal(charge: {
  matchStatus: string;
  matchedPaymentId: string | null;
  clearedInReconciliationId: string | null;
  expenseMatchCount: number;
}): string | null {
  if (charge.clearedInReconciliationId) {
    return 'this charge was cleared in a completed reconciliation, so it takes no new expense match until that reconciliation is reopened with a reason';
  }
  if (charge.matchStatus === 'Unmatched') return null;
  if (charge.matchStatus === 'Matched' && charge.matchedPaymentId === null && charge.expenseMatchCount > 0) return null;
  if (charge.matchStatus === 'Matched' && charge.matchedPaymentId !== null) {
    return 'this charge is already matched to a payment, and that payment is what posted the money; matching an expense line to it now would post the same spend a second time';
  }
  if (charge.matchStatus === 'Categorized') return 'this charge is already categorized, and that entry is what posted it; reverse the entry before matching an expense line to it';
  if (charge.matchStatus === 'Transfer') return 'this charge is one half of a bank transfer, and the transfer posted the money; an expense line matched to it would post the same money twice';
  if (charge.matchStatus === 'Excluded') return 'this charge is excluded as a duplicate, so it counts nowhere and takes no expense match';
  return `this charge is ${charge.matchStatus} and only a charge the feed has not settled takes an expense match`;
}

/**
 * 🔨 A filer reads their own reports and nobody else's (decision 0505). Reading
 * across everybody's is the per-module view override, which the Accountant, HR
 * and Admin rows carry and a role an administrator makes for a filer does not.
 */
export function reportReadRefusal(facts: { actorPersonId: string; createdByPersonId: string; readsEveryReport: boolean }): string | null {
  if (facts.readsEveryReport || facts.actorPersonId === facts.createdByPersonId) return null;
  return 'this report belongs to somebody else, and reading across everybody’s reports is the accounting and HR side’s own read';
}

/**
 * 🔨 A filer edits and submits their own report (decision 0505). The accounting
 * side edits anybody's, because the accountant is who fixes a report before
 * approving it; nobody else files on somebody else's behalf.
 */
export function reportEditRefusal(facts: { actorPersonId: string; createdByPersonId: string; editsEveryReport: boolean }): string | null {
  if (facts.editsEveryReport || facts.actorPersonId === facts.createdByPersonId) return null;
  return 'this report belongs to somebody else; filing on another person’s behalf is the accounting side’s own write';
}
